import { Box, Button, Typography, useTheme } from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import React, { useMemo } from 'react'

import thinkingSimba from '../../../../../shared/assets/simba-ai/Thinking.png'
import { useSimbaAIStore } from '../../../../store/simbaai.store'
import { Icons } from '../../../../../shared/utils/icons.jsx'
import { useUserStore } from '../../../../store/user.store'

const SubjectTopics = () => {

    const { palette, layout, border, shadows } = useTheme()

    const SimbaAIStore = useSimbaAIStore()
    const UserStore = useUserStore()

    const [searchParams, setSearchParams] = useSearchParams()
    const selectedSubject = searchParams.get( 'selectedSubject' )

    let subjectDetails = UserStore.getUserSubjectsDetails.allSubjects?.find( sub => sub.subject_code === selectedSubject )
    if ( !subjectDetails ) {
        subjectDetails = SimbaAIStore.getPracticeSessions.filter( ps => ps.subject.subject_code === selectedSubject ).at( 0 )?.subject
        if ( !subjectDetails )
            subjectDetails = { subject_code: selectedSubject }
    }

    const setSearchParamsValues = ( values ) => {
        setSearchParams( prev => {
            const newParams = new URLSearchParams( prev )
            for ( const key in values ) {
                if ( !Object.hasOwn( values, key ) ) continue
                if ( values[key] )
                    newParams.set( key, values[key] )
                else
                    newParams.delete( key )
            }
            return newParams
        }, { replace: true } )
    }

    const backToHome = () => {
        setSearchParamsValues( { selectedSubject: "" } )
        SimbaAIStore.setView( "home", "right" )
    }



    const subjectTopics = useMemo( () => {
        const recentPracticeSessions = SimbaAIStore.getPracticeSessions.filter( ps => ps.subject.subject_code === selectedSubject )
        const recentTopics = []
        for ( const ps of recentPracticeSessions ) {
            for ( const module in ps.modules_and_topics ) {
                if ( !Object.hasOwn( ps.modules_and_topics, module ) ) continue
                const topics = ps.modules_and_topics[module].topics
                recentTopics.push( ...topics.map( topic => ( { topic, module, subject_name: ps.subject.subject_name, subject_code: ps.subject.subject_code } ) ) )
            }
        }
        return recentTopics
    }, [SimbaAIStore.getPracticeSessions, selectedSubject] )

    const fetchAnswerFromAI = async ( topicDetails ) => {
        if ( SimbaAIStore.getAiResponsesQueue.request ) {
            SimbaAIStore.addResponse( { topic: SimbaAIStore.getAiResponsesQueue.request, response: SimbaAIStore.getAiResponsesQueue.response } )
            SimbaAIStore.setAiResponsesQueue( { request: "", response: "" } )
        }
        try {
            SimbaAIStore.setView( 'fetching-simba-ai-response', "left", false )
            SimbaAIStore.setSuggestedTopics( subjectTopics, { category: "subjectTopics", name: subjectDetails.subject_name } )
            if ( await SimbaAIStore.getAIResponse( topicDetails ) )
                SimbaAIStore.setView( 'simba-ai-response', "left", false )
            else
                SimbaAIStore.setView( 'simba-ai-response-error', "left", false )

        } catch ( err ) {
            console.log( err )
        }
    }

    return (
        <Box
            sx={{
                minHeight: 0,
                display: "flex",
                padding: layout.pagePadding,
                flexGrow: 1,
                flexDirection: "column",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                gap: "20px"
            }}>

            <Box
                sx={{
                    flexShrink: 0,
                    width: "100%",
                    padding: layout.pagePadding,
                    borderRadius: "10px",
                    gap: "20px",
                    alignItems: "center",
                    display: "flex",
                    background: palette.primary.light,
                    borderBottom: `4px solid ${palette.primary.light + "99"}`,
                    borderLeft: `4px solid ${palette.primary.light + "99"}`
                }}>
                <Box component="img" src={thinkingSimba} sx={{
                    height: "120px"
                }} />
                <Box>
                    <Typography
                        color={palette.common.white}
                        sx={{
                            lineHeight: 1,
                            fontSize: "28px",
                            fontWeight: 800
                        }}>{subjectDetails.subject_name || subjectDetails.subject_code}</Typography>
                    <Typography
                        color={palette.common.white}
                        sx={{
                            fontSize: "12px",
                            fontWeight: 500
                        }}>{subjectTopics.length} topics available.</Typography>

                    <Box
                        sx={{
                            flexWrap: "wrap",
                            flexGrow: { xs: "1", sm: "initial" },
                            marginTop: "10px",
                            display: "flex",
                            gap: layout.cardsGap,
                            alignItems: "center"
                        }}>
                        <Button onClick={backToHome} sx={{ color: palette.common.white, border: `1px solid ${palette.common.white}`, borderBottom: `4px solid ${palette.common.white}`, borderRadius: "10px" }} startIcon={Icons.default.ArrowBack}  >Back to home</Button>
                    </Box>
                </Box>
            </Box>

            {subjectTopics.length > 0 && <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    marginTop: "10px"
                }}>
                <Typography
                    color='textSecondary'
                    component="p"
                    sx={{
                        fontStyle: "12px",
                        marginBottom: "10px",
                        fontWeight: 800,
                        fontSize: "16px"
                    }}>
                    Select a {subjectDetails.subject_name || subjectDetails.subject_code} topic to learn it.
                </Typography>

                <Box
                    sx={{
                        display: "flex",
                        gridTemplateColumns: { md: "repeat(3,1fr)", xs: "1fr", sm: "1fr 1fr" },
                        flexWrap: "wrap",
                        width: "100%",
                        gap: layout.cardsGap
                    }}>
                    {subjectTopics.map( ( topicDetails ) => {

                        return (
                            <Box
                                component="button"
                                onClick={() => fetchAnswerFromAI( topicDetails )}
                                key={topicDetails.topic}
                                sx={{
                                    flexGrow: 1,
                                    overflow: "hidden",
                                    padding: "10px 20px",
                                    borderRadius: "10px",
                                    boxShadow: shadows[1],
                                    border: border[1],
                                    borderBottom: `4px solid ${palette.borderColor}`,
                                    position: "relative",
                                    bgcolor: palette.contentBg,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    cursor: "pointer",
                                    transition: "200ms",

                                    ":hover": {
                                        borderColor: palette.primary.light,
                                        transform: `skew(-5deg)`
                                    }
                                }}>
                                <Typography title={topicDetails.topic} noWrap color='textPrimary' sx={{
                                    fontSize: "16px"
                                }} >{topicDetails.topic}</Typography>
                                <Typography
                                    title={topicDetails.subject_name}
                                    noWrap
                                    color='textSecondary'
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "5px",
                                        fontSize: "12px"
                                    }}>{topicDetails.subject_name}</Typography>
                            </Box>
                        );
                    } )}
                </Box>
            </Box>}
        </Box>
    );
}

export default SubjectTopics
